import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { StatefulComponent } from '../../components/stateful/stateful.component';
import { InputComponent } from '../../components/input/input.component';
import { AddVehicleModalComponent } from '../../components/add-vehicle-modal/add-vehicle-modal.component';
import { ConfirmationModalComponent } from '../../components/confirmation-modal/confirmation-modal.component';
import { WorkItemFormFieldsComponent } from '../../components/work-item-form-fields/work-item-form-fields.component';
import {
  buildWorkItemFormControls,
  composeShopHours,
  isFlatMode,
  parseMoney
} from '../../components/work-item-form-fields/work-item-form';
import { setContainerTypeAction, showLoadingMaskAction } from '../../ngrx/actions/global.actions';
import { AuthorizeService } from '../../services/authorize.service';
import { AlertService } from '../../services/alert.service';
import {
  InvoiceService,
  InvoiceStatusResponseDto,
  InvoiceVehicleResponseDto,
  WorkItemStatusResponseDto
} from '../../services/invoice.service';
import { VehicleResponseDto, VehicleService } from '../../services/vehicle.service';
import { Customer, CreateCustomerInput } from '../../models/Customer.model';

// New customers need at least one way to be contacted; existing-customer
// selection leaves these fields blank, so only enforce when creating one.
function newCustomerContactMethod(control: AbstractControl): ValidationErrors | null {
  if (!control.get('newCustomer')?.value) {
    return null;
  }
  const email = control.get('email')?.value?.trim();
  const phone = control.get('phone')?.value?.trim();
  return email || phone ? null : { atLeastOneContact: true };
}

// A work item staged in the wizard before the quote is submitted.
interface QuoteWorkItem {
  vehicleId: string;
  title: string;
  empUserId: string;
  miles: number | null;
  shopHours: number | null;
  partsPricing: number | null;
  // Flat-fee override: replaces labor + parts as the line price when set.
  flatFee: number | null;
  flatFeeTaxable: boolean;
  notes: string;
}

@Component({
  selector: 'app-create-quote',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, InputComponent, WorkItemFormFieldsComponent],
  templateUrl: './create-quote.component.html',
  styleUrl: './create-quote.component.scss'
})
export class CreateQuoteComponent extends StatefulComponent implements OnInit {
  readonly steps = ['Customer', 'Vehicles', 'Work Items', 'Review'];
  currentStep = 1;

  customers: Customer[] = [];
  mechanics: Customer[] = [];
  invoiceStatuses: InvoiceStatusResponseDto[] = [];
  workItemStatuses: WorkItemStatusResponseDto[] = [];

  // Vehicles belonging to the customer selected in step 1.
  vehicles: VehicleResponseDto[] = [];
  private vehiclesLoadedForUserId: string | null = null;
  selectedVehicleIds: string[] = [];

  workItems: QuoteWorkItem[] = [];

  isSubmitting = false;

  // Step 1: pick an existing customer or create a new one. Email is optional
  // (walk-in customers may only have a phone) but at least one contact method
  // is required — see newCustomerContactMethod.
  customerForm: FormGroup = new FormGroup({
    newCustomer: new FormControl(false),
    selectedCustomer: new FormControl(''),
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.email]),
    phone: new FormControl('', [Validators.pattern(/^[\d\s().+-]{7,20}$/)])
  }, { validators: newCustomerContactMethod });

  // Step 3: stage work items one at a time against a selected vehicle. The
  // fields themselves (pricing modes, live summary) are the shared
  // work-item-form-fields component, same as the edit modal.
  workItemForm: FormGroup = new FormGroup({
    vehicleId: new FormControl(''),
    ...buildWorkItemFormControls()
  });

  constructor(
    private authService: AuthorizeService,
    private alertService: AlertService,
    private invoiceService: InvoiceService,
    private vehicleService: VehicleService,
    private ngbModal: NgbModal,
    private router: Router
  ) {
    super();
  }

  // Sales tax rate for the estimate preview; fetched from the API so the
  // rate lives in exactly one place (TAX_RATE env). $0 tax until it loads.
  taxRate = 0;

  ngOnInit() {
    this.dispatch(setContainerTypeAction({ containerType: 'normal' }));
    this.loadCustomers();
    this.loadMechanics();
    this.loadStatuses();
    this.subs.push(this.invoiceService.getPricingConfig().subscribe({
      next: (config) => this.taxRate = config.taxRate,
      error: (error) => console.error('[CreateQuote] Failed to load pricing config:', error)
    }));
  }

  // ============= LOOKUPS =============

  private loadCustomers(): void {
    this.subs.push(this.authService.getCustomers().subscribe({
      next: (customers) => this.customers = customers,
      error: (error) => {
        console.error('[CreateQuote] Failed to load customers:', error);
        this.alertService.showSnackBar('Failed to load customers.');
      }
    }));
  }

  private loadMechanics(): void {
    this.subs.push(this.authService.getMechanics().subscribe({
      next: (mechanics) => this.mechanics = mechanics,
      error: (error) => {
        console.error('[CreateQuote] Failed to load mechanics:', error);
        this.alertService.showSnackBar('Failed to load mechanics.');
      }
    }));
  }

  private loadStatuses(): void {
    this.subs.push(this.invoiceService.getInvoiceStatuses().subscribe({
      next: (statuses) => this.invoiceStatuses = statuses,
      error: (error) => console.error('[CreateQuote] Failed to load invoice statuses:', error)
    }));
    this.subs.push(this.invoiceService.getWorkItemStatuses().subscribe({
      next: (statuses) => this.workItemStatuses = statuses,
      error: (error) => console.error('[CreateQuote] Failed to load work item statuses:', error)
    }));
  }

  // ============= STEP NAVIGATION =============

  goToStep(step: number): void {
    // The stepper only allows jumping backwards; forward moves go through next().
    if (step < this.currentStep) {
      this.currentStep = step;
    }
  }

  back(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  next(): void {
    switch (this.currentStep) {
      case 1:
        this.completeCustomerStep();
        break;
      case 2:
        this.completeVehicleStep();
        break;
      case 3:
        this.completeWorkItemStep();
        break;
    }
  }

  // ============= STEP 1: CUSTOMER =============

  get isNewCustomer(): boolean {
    return this.customerForm.get('newCustomer')?.value === true;
  }

  get selectedCustomerId(): string {
    return this.customerForm.get('selectedCustomer')?.value || '';
  }

  get selectedCustomer(): Customer | undefined {
    return this.customers.find(c => c.userId === this.selectedCustomerId);
  }

  getCustomerDisplayName(customer: Customer): string {
    const contact = customer.email || customer.phoneNumber || 'no contact info';
    return `${customer.firstName} ${customer.lastName} (${contact})`;
  }

  private completeCustomerStep(): void {
    if (this.isNewCustomer) {
      this.createNewCustomer();
      return;
    }

    if (!this.selectedCustomerId) {
      this.alertService.showSnackBar('Please select a customer.');
      return;
    }

    this.loadVehiclesForCustomer();
    this.currentStep = 2;
  }

  private createNewCustomer(): void {
    const requiredControls = ['firstName', 'lastName'];
    for (const control of requiredControls) {
      if (!this.customerForm.get(control)?.valid) {
        this.customerForm.get(control)?.markAsDirty();
        this.alertService.showSnackBar(`Error with ${control} input`);
        return;
      }
    }
    // Email/phone are optional but must be well-formed when filled, and at
    // least one of them is required.
    for (const control of ['email', 'phone']) {
      if (!this.customerForm.get(control)?.valid) {
        this.customerForm.get(control)?.markAsDirty();
        this.alertService.showSnackBar(`Error with ${control} input`);
        return;
      }
    }
    if (this.customerForm.hasError('atLeastOneContact')) {
      this.customerForm.get('email')?.markAsDirty();
      this.customerForm.get('phone')?.markAsDirty();
      this.alertService.showSnackBar('Please provide an email address or phone number.');
      return;
    }

    this.dispatch(showLoadingMaskAction({ showLoadingMask: true }));

    const email = this.customerForm.get('email')?.value?.trim();
    const phone = this.customerForm.get('phone')?.value?.trim();
    const newCustomer: CreateCustomerInput = {
      Email: email || undefined,
      PhoneNumber: phone || undefined,
      UserName: email || phone,
      FirstName: this.customerForm.get('firstName')?.value,
      LastName: this.customerForm.get('lastName')?.value
    };

    // createCustomer returns the new userId; re-fetch the list so the
    // dropdown can display the new customer.
    this.subs.push(this.authService.createCustomer(newCustomer).pipe(
      switchMap(({ userId }) => this.authService.getCustomers().pipe(
        map((customers) => ({ userId, customers }))
      ))
    ).subscribe({
      next: ({ userId, customers }) => {
        this.dispatch(showLoadingMaskAction({ showLoadingMask: false }));
        this.customers = customers;

        this.alertService.showSnackBar('Customer created successfully!');
        this.customerForm.get('newCustomer')?.setValue(false);
        this.customerForm.get('selectedCustomer')?.setValue(userId);
        this.loadVehiclesForCustomer();
        this.currentStep = 2;
      },
      error: (error) => {
        this.dispatch(showLoadingMaskAction({ showLoadingMask: false }));
        console.error('[CreateQuote] Failed to create customer:', error);
        this.alertService.showSnackBar('Failed to create customer.');
      }
    }));
  }

  // ============= STEP 2: VEHICLES =============

  private loadVehiclesForCustomer(): void {
    const custUserId = this.selectedCustomerId;

    // Selecting a different customer invalidates any staged vehicles/work items.
    if (this.vehiclesLoadedForUserId && this.vehiclesLoadedForUserId !== custUserId) {
      this.selectedVehicleIds = [];
      this.workItems = [];
    }
    if (this.vehiclesLoadedForUserId === custUserId) {
      return;
    }

    this.vehiclesLoadedForUserId = custUserId;
    this.subs.push(this.vehicleService.getVehiclesByUserId(custUserId).subscribe({
      next: (vehicles) => this.vehicles = vehicles,
      error: (error) => {
        console.error('[CreateQuote] Failed to load vehicles:', error);
        this.alertService.showSnackBar('Failed to load vehicles for customer.');
      }
    }));
  }

  getVehicleDisplayName(vehicle: VehicleResponseDto): string {
    const parts = [vehicle.Year, vehicle.Make, vehicle.Model].filter(p => !!p);
    return parts.length > 0 ? parts.join(' ') : 'Unknown Vehicle';
  }

  getVehicleById(vehicleId: string): VehicleResponseDto | undefined {
    return this.vehicles.find(v => v.VehicleId === vehicleId);
  }

  isVehicleSelected(vehicleId: string): boolean {
    return this.selectedVehicleIds.includes(vehicleId);
  }

  toggleVehicle(vehicleId: string): void {
    if (this.isVehicleSelected(vehicleId)) {
      this.selectedVehicleIds = this.selectedVehicleIds.filter(id => id !== vehicleId);
      // Drop any staged work items for the deselected vehicle.
      this.workItems = this.workItems.filter(wi => wi.vehicleId !== vehicleId);
    } else {
      this.selectedVehicleIds = [...this.selectedVehicleIds, vehicleId];
    }
  }

  get selectedVehicles(): VehicleResponseDto[] {
    return this.vehicles.filter(v => this.isVehicleSelected(v.VehicleId));
  }

  openAddVehicleModal(): void {
    const modalRef = this.ngbModal.open(AddVehicleModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });

    modalRef.componentInstance.modalRef = modalRef;
    modalRef.componentInstance.presetCustUserId = this.selectedCustomerId;

    // New vehicles are auto-selected for the quote.
    this.subs.push(modalRef.componentInstance.vehicleAdded.subscribe((vehicle: VehicleResponseDto) => {
      this.vehicles = [...this.vehicles, vehicle];
      this.selectedVehicleIds = [...this.selectedVehicleIds, vehicle.VehicleId];
    }));
  }

  private completeVehicleStep(): void {
    if (this.selectedVehicleIds.length === 0) {
      this.alertService.showSnackBar('Please select at least one vehicle.');
      return;
    }

    // Default the work item form's vehicle to the only/first selected vehicle.
    const currentVehicleId = this.workItemForm.get('vehicleId')?.value;
    if (!currentVehicleId || !this.isVehicleSelected(currentVehicleId)) {
      this.workItemForm.get('vehicleId')?.setValue(this.selectedVehicleIds[0]);
    }
    this.currentStep = 3;
  }

  // ============= STEP 3: WORK ITEMS =============

  getMechanicDisplayName(empUserId: string): string {
    const mechanic = this.mechanics.find(m => m.userId === empUserId);
    return mechanic ? `${mechanic.firstName} ${mechanic.lastName}` : 'Unassigned';
  }

  workItemsForVehicle(vehicleId: string): QuoteWorkItem[] {
    return this.workItems.filter(wi => wi.vehicleId === vehicleId);
  }

  /** Stages the current form as a work item; false when validation blocks it. */
  addWorkItem(): boolean {
    const vehicleId = this.workItemForm.get('vehicleId')?.value;
    if (!vehicleId) {
      this.alertService.showSnackBar('Please select a vehicle for the work item.');
      return false;
    }

    if (!this.workItemForm.get('title')?.valid) {
      this.workItemForm.get('title')?.markAsDirty();
      this.alertService.showSnackBar('Please enter a title for the work item.');
      return false;
    }

    const numericControls = ['miles', 'shopHours', 'partsPricing', 'flatFee'];
    for (const control of numericControls) {
      // .invalid, not !.valid: the inactive pricing mode's controls are
      // disabled, and disabled controls are neither valid nor invalid.
      if (this.workItemForm.get(control)?.invalid) {
        this.workItemForm.get(control)?.markAsDirty();
        this.alertService.showSnackBar(`Error with ${control} input`);
        return false;
      }
    }

    const flat = isFlatMode(this.workItemForm);
    if (flat && parseMoney(this.workItemForm.get('flatFee')?.value) == null) {
      this.alertService.showSnackBar('Please enter a flat fee amount.');
      return false;
    }

    const miles = this.workItemForm.get('miles')?.value;
    // Disabled controls still expose .value, so the inactive mode's retained
    // inputs flow through as reference data (matches the edit modal).
    const shopHours = composeShopHours(this.workItemForm);
    const partsPricing = parseMoney(this.workItemForm.get('partsPricing')?.value);
    // Flat-fee override only applies in flat mode.
    const flatFee = flat ? parseMoney(this.workItemForm.get('flatFee')?.value) ?? null : null;

    this.workItems = [...this.workItems, {
      vehicleId,
      title: this.workItemForm.get('title')?.value,
      empUserId: this.workItemForm.get('empUserId')?.value || '',
      miles: miles !== '' && miles !== null ? parseInt(miles, 10) : null,
      shopHours: shopHours ?? null,
      partsPricing: partsPricing ?? null,
      flatFee,
      // A stale checkbox without a fee must not persist as taxable.
      flatFeeTaxable: flatFee != null && this.workItemForm.get('flatFeeTaxable')?.value === true,
      notes: this.workItemForm.get('notes')?.value || ''
    }];

    // Keep the vehicle and mechanic selections to make adding several similar
    // work items quick; clear the descriptive fields.
    this.workItemForm.patchValue({
      title: '', miles: '', shopHours: '', shopMinutes: '', partsPricing: '',
      flatFee: '', flatFeeTaxable: false, notes: ''
    });
    // Must emit so the shared fields component re-enables the computed branch.
    this.workItemForm.get('pricingMode')?.setValue('computed');
    // Pristine again so the step 3 -> 4 unsaved-work-item prompt stays quiet.
    this.workItemForm.markAsPristine();
    return true;
  }

  removeWorkItem(workItem: QuoteWorkItem): void {
    this.workItems = this.workItems.filter(wi => wi !== workItem);
  }

  private completeWorkItemStep(): void {
    // Work items are optional at quote time — they can be added later while
    // the invoice is in MECHANIC_REVIEW. But a dirty, un-staged form with
    // nothing added yet usually means the mechanic forgot to click Add.
    if (this.workItems.length === 0 && this.workItemForm.dirty) {
      const confirmRef = this.ngbModal.open(ConfirmationModalComponent, { centered: true });
      confirmRef.componentInstance.modalRef = confirmRef;
      confirmRef.componentInstance.title = 'Unsaved Work Item';
      confirmRef.componentInstance.message =
        'You started a work item but have not added it to the quote yet. Add it before continuing?';
      confirmRef.componentInstance.confirmButtonText = 'Add & Continue';
      confirmRef.componentInstance.cancelButtonText = 'Continue Without Adding';
      confirmRef.componentInstance.confirmButtonClass = 'btn-primary';
      confirmRef.result
        .then((confirmed: boolean) => {
          if (confirmed) {
            // Invalid form: addWorkItem() already showed a snackbar — stay here.
            if (this.addWorkItem()) {
              this.currentStep = 4;
            }
          } else {
            this.currentStep = 4;
          }
        })
        .catch(() => { /* esc / backdrop dismiss: stay on step 3 */ });
      return;
    }
    this.currentStep = 4;
  }

  // ============= STEP 4: REVIEW & SUBMIT =============

  // Flat-fee items are priced by their fee alone, so the parts/labor sums
  // skip them; the fee shows in its own row.
  get estimatedPartsTotal(): number {
    return this.workItems.reduce((total, wi) =>
      wi.flatFee != null ? total : total + (wi.partsPricing || 0), 0);
  }

  // Labor: staged shop hours x the assigned mechanic's hourly rate.
  get estimatedLaborTotal(): number {
    return this.workItems.reduce((total, wi) => {
      if (wi.flatFee != null) return total;
      const rate = this.mechanics.find(m => m.userId === wi.empUserId)?.hourlyRate || 0;
      return total + (wi.shopHours || 0) * rate;
    }, 0);
  }

  get estimatedFlatFeeTotal(): number {
    return this.workItems.reduce((total, wi) => total + (wi.flatFee ?? 0), 0);
  }

  get estimatedSubtotal(): number {
    return this.estimatedLaborTotal + this.estimatedPartsTotal + this.estimatedFlatFeeTotal;
  }

  // Tax applies to parts only — or to the whole flat fee when marked taxable.
  // Same math as the API's recalcInvoiceTotals.
  get estimatedTax(): number {
    const base = this.workItems.reduce((total, wi) =>
      wi.flatFee != null
        ? total + (wi.flatFeeTaxable ? wi.flatFee : 0)
        : total + (wi.partsPricing || 0), 0);
    return Math.round(base * this.taxRate * 100) / 100;
  }

  get estimatedTotal(): number {
    return this.estimatedSubtotal + this.estimatedTax;
  }

  get totalShopHours(): number {
    return this.workItems.reduce((total, wi) => total + (wi.shopHours || 0), 0);
  }

  // Render a decimal hour total as "6 hr 30 min" for the invoice TimeEstimate.
  formatShopTime(totalHours: number): string {
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);
    const parts: string[] = [];
    if (hours > 0) {
      parts.push(`${hours} hr`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} min`);
    }
    return parts.join(' ');
  }

  submitQuote(): void {
    const custUserId = this.selectedCustomerId;
    // New quotes start in MECHANIC_REVIEW (the first board phase); their work
    // items start in the TODO work-item status.
    const mechanicReviewStatus = this.invoiceStatuses.find(s => s.Status === 'MECHANIC_REVIEW');
    const todoWorkItemStatus = this.workItemStatuses.find(s => s.Status === 'TODO');

    if (!custUserId || !mechanicReviewStatus || !todoWorkItemStatus) {
      this.alertService.showSnackBar('Unable to create quote: reference data is missing.');
      return;
    }

    this.isSubmitting = true;
    this.dispatch(showLoadingMaskAction({ showLoadingMask: true }));

    // Create the invoice, then the connecting invoiceVehicle row for each
    // selected vehicle, then the work items against those invoiceVehicle ids.
    // The invoice's time estimate is the aggregate shop time across all work items.
    const totalShopHours = this.totalShopHours;

    this.subs.push(this.invoiceService.createInvoice({
      CustUserId: custUserId,
      InvoiceStatusId: mechanicReviewStatus.InvoiceStatusId,
      TimeEstimate: totalShopHours > 0 ? this.formatShopTime(totalShopHours) : undefined
    }).pipe(
      switchMap((invoice) => forkJoin(
        this.selectedVehicleIds.map(vehicleId => this.invoiceService.addVehicleToInvoice({
          InvoiceId: invoice.InvoiceId,
          VehicleId: vehicleId
        }))
      )),
      switchMap((invoiceVehicles: InvoiceVehicleResponseDto[]) => {
        const invoiceVehicleIdsByVehicle = new Map(
          invoiceVehicles.map(iv => [iv.VehicleId, iv.InvoiceVehicleId])
        );

        const workItemRequests = this.workItems.map(wi => this.invoiceService.createWorkItem({
          InvoiceVehicleId: invoiceVehicleIdsByVehicle.get(wi.vehicleId)!,
          WorkItemStatusId: todoWorkItemStatus.WorkItemStatusId,
          EmpUserId: wi.empUserId || undefined,
          Title: wi.title,
          Miles: wi.miles ?? undefined,
          ShopHours: wi.shopHours ?? undefined,
          PartsPricing: wi.partsPricing ?? undefined,
          FlatFee: wi.flatFee ?? undefined,
          FlatFeeTaxable: wi.flatFeeTaxable,
          Notes: wi.notes || undefined
        }));

        return workItemRequests.length > 0 ? forkJoin(workItemRequests) : of([]);
      })
    ).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.dispatch(showLoadingMaskAction({ showLoadingMask: false }));
        this.alertService.showSnackBar('Quote created successfully!');
        this.router.navigate(['/invoices']);
      },
      error: (error) => {
        this.isSubmitting = false;
        this.dispatch(showLoadingMaskAction({ showLoadingMask: false }));
        console.error('[CreateQuote] Failed to create quote:', error);
        this.alertService.showSnackBar('Failed to create quote. Please try again.');
      }
    }));
  }
}
